import React from 'react';
import { cn } from '../../../lib/utils';
import { MobileDashboardSkeleton } from '../../SkeletonLoader/SkeletonLoader';

const LoadingScreen = ({ message = 'Loading' }) => {
  // Get sidebar collapsed state from localStorage (default to collapsed)
  const getSidebarCollapsed = () => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? saved === 'true' : true; // Default to collapsed
  };
  
  const isCollapsed = getSidebarCollapsed();

  return (
    <div
      className="fixed inset-0 z-50 dark:bg-slate-900"
      style={{ background: 'var(--lodge-onyx-50)' }}
    >
      {/* Excel-like grid overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `
            linear-gradient(to right, rgba(100, 116, 139, 0.07) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(100, 116, 139, 0.07) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 100%), linear-gradient(to bottom, black 0%, black 90%, transparent 100%)',
          WebkitMaskComposite: 'destination-in',
          maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 100%), linear-gradient(to bottom, black 0%, black 90%, transparent 100%)',
          maskComposite: 'intersect',
        }}
      />
      {/* Desktop Layout */}
      <div className="hidden md:flex h-full">
        {/* Floating Sidebar Skeleton */}
        <div 
          className="fixed left-4 top-4 h-[calc(100vh-2rem)] border transition-all duration-300 ease-in-out z-40 bg-white/95 backdrop-blur-xl border-slate-200/60 dark:bg-slate-900/90 dark:border-slate-700/50 shadow-xl rounded-2xl flex flex-col"
          style={{ width: isCollapsed ? 80 : 256 }}
        >
          {/* Sidebar header */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200/60 dark:border-slate-800/60 rounded-t-2xl">
            {!isCollapsed ? (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-26 h-18 bg-slate-300 dark:bg-slate-700 rounded animate-pulse"></div>
                </div>
                <div className="w-8 h-8 bg-slate-300 dark:bg-slate-700 rounded-lg animate-pulse"></div>
              </>
            ) : (
              <div className="w-8 h-8 bg-slate-300 dark:bg-slate-700 rounded animate-pulse mx-auto"></div>
            )}
          </div>
          
          {/* Navigation items */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {/* Dashboard - active */}
            <div className={cn("flex items-center p-3 rounded-lg bg-orange-50 dark:bg-orange-950/50", isCollapsed && "justify-center")}>
              <div className="w-5 h-5 bg-orange-400 rounded animate-pulse"></div>
              {!isCollapsed && <div className="ml-3 w-20 h-4 bg-orange-400 rounded animate-pulse"></div>}
            </div>
            
            {/* Other nav items */}
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={cn("flex items-center p-3 rounded-lg", isCollapsed && "justify-center")}>
                <div className="w-5 h-5 bg-slate-300 dark:bg-slate-700 rounded animate-pulse"></div>
                {!isCollapsed && <div className="ml-3 w-16 h-4 bg-slate-300 dark:bg-slate-700 rounded animate-pulse"></div>}
              </div>
            ))}
          </nav>
          
          {/* Bottom section - Organization/User */}
          <div className="border-t border-slate-200/60 dark:border-slate-800/60 p-3">
            <div className={cn("flex items-center gap-3 p-3 rounded-lg", isCollapsed && "justify-center")}>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 animate-pulse shrink-0"></div>
              {!isCollapsed && (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="w-32 h-4 bg-slate-300 dark:bg-slate-700 rounded animate-pulse mb-1"></div>
                    <div className="w-24 h-3 bg-slate-200 dark:bg-slate-600 rounded animate-pulse"></div>
                  </div>
                  <div className="w-4 h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse shrink-0"></div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Floating icon tray skeleton (search · bell · chat pill, top-right) */}
        <div className="fixed top-4 right-4 h-12 flex items-center gap-1 px-1.5 z-30 bg-white/95 backdrop-blur-xl border border-slate-200/60 dark:bg-slate-900/90 dark:border-slate-700/50 shadow-xl rounded-full">
          <div className="w-9 h-9 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700"></div>
          <div className="w-9 h-9 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
          <div className="w-9 h-9 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
        </div>

        {/* Main content area - centered loading message */}
        <div
          className="flex-1 flex items-center justify-center mt-6 mr-4"
          style={{ marginLeft: isCollapsed ? 112 : 288 }}
        >
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600 dark:text-slate-400 font-medium">{message}...</p>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden min-h-screen">
        {/* Top Bar - matches MobileLayout hamburger header */}
        <header className="fixed top-0 left-0 right-0 h-14 bg-white/95 backdrop-blur-xl border-b border-slate-200/60 dark:bg-slate-900/95 dark:border-slate-700/50 z-40 flex items-center px-3">
          {/* Left: Hamburger skeleton + Logo */}
          <div className="p-2 -ml-1">
            <div className="w-[22px] h-[22px] bg-slate-300 dark:bg-slate-700 rounded animate-pulse"></div>
          </div>
          <div className="flex items-center gap-2 ml-1">
            <div className="w-7 h-7 bg-slate-300 dark:bg-slate-700 rounded animate-pulse"></div>
            <div className="w-16 h-5 bg-slate-300 dark:bg-slate-700 rounded animate-pulse"></div>
          </div>

          {/* Right: Search & Notification skeletons */}
          <div className="flex items-center gap-1 ml-auto">
            <div className="p-2">
              <div className="w-5 h-5 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
            </div>
            <div className="p-2">
              <div className="w-5 h-5 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
            </div>
          </div>
        </header>

        {/* Mobile Main Content - dashboard skeleton with connecting overlay */}
        <main className="pt-14 min-h-screen">
          <div className="relative overflow-hidden px-3 pt-4">
            <MobileDashboardSkeleton />
            {/* Connecting overlay */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'var(--lodge-onyx-50, #f8fafc)' }}>
              <div className="text-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm rounded-2xl px-8 py-6 shadow-lg border border-slate-200/60 dark:border-slate-700/50">
                <div className="w-10 h-10 border-3 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-slate-600 dark:text-slate-400 font-medium text-sm">{message}...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LoadingScreen; 